import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from 'react';
import { FaUserAlt } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { usePDF } from 'react-to-pdf';

import Header from '../../components/Header';
import Footer from '../../components/Footer';
import TopPage from '../../components/TopPage';
import { fetchApi } from '../../services/api';
import { getLink } from '../../services/host';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';

import './style.css';
import { calculateAge } from '../../utils/age';

const DiagnosticoPaciente = () => {
  const params = useParams();

  const refShowPdf = useRef(null);

  const [state, setState] = useState({
    id: params.id !== undefined ? params.id : null,
    isLoading: false,
    id_paciente: null,
    nome_paciente: null,
    escolaridade: null,
    nascimento: null,
    idade_teste: '',
    idade: '',
    data_teste: '-',
    proximo_modulo: null,
    acertos: [0, 0, 0],
    medias: [0.0, 0.0],
    indices: [0.0, 0.0],
    need_validation: false,
    pontuacao: [0, 0, 0, 0, 0, 0, 0],
    classificacao: ['-', '-', '-', '-', '-', '-', '-'],
    variabilidade: [0, 0],
  });

  const { toPDF, targetRef } = usePDF({
    filename: `resultados-avaliacao-paciente-${state.nome_paciente}.pdf`,
    page: {
      format: 'a4',
      orientation: 'portrait',
      margin: 10,
    },
  });

  const carregaDados = useCallback(() => {
    setState((prev) => ({ ...prev, isLoading: true }));

    fetchApi
      .get(process.env.REACT_APP_TESTE_DIAGNOSTICO, state.id)
      .then((response) => {
        if (response.data.status === 'success') {
          setState((prev) => ({
            ...prev,
            module_complete: response.data.modulos,
            id_paciente: response.data.paciente.id_paciente,
            nascimento: response.data.paciente.nascimento,
            escolaridade: response.data.paciente.escolaridade,
            nome_paciente: response.data.paciente.nome,
            acertos: response.data.acertos,
            medias: response.data.medias,
            indices: response.data.indices,
            idade: response.data.idade,
            idade_teste: response.data.idade_teste,
            data_teste: response.data.teste.created_at,
            need_validation: response.data.needValidation,
            pontuacao: response.data.pontuacaoPadrao,
            classificacao: response.data.classificacao,
            variabilidade: response.data.variabilidade,
          }));
        }
      })
      .catch((err) => {})
      .finally(() => {
        setState((prev) => ({ ...prev, isLoading: false }));
      });
  }, [state.id]);

  useEffect(() => {
    document.title = `Resultados da avaliação`;
    window.scrollTo(0, 0);

    carregaDados();
  }, [carregaDados]);

  const handleExportPDF = () => {
    refShowPdf.current.style.display = 'block';
    toPDF();
    refShowPdf.current.style.display = 'none';
  };

  const isAgeValid =
    calculateAge(state.nascimento) >= 11 &&
    calculateAge(state.nascimento) <= 30;

  return (
    <div>
      <Header />

      <TopPage
        title={state.nome_paciente}
        back={getLink(`/teste/${state.id}/modulos`)}
        small={`Data de nascimento: ${state.nascimento} - ${state.idade}`}
        small2={`Idade ao realizar o teste: ${state.idade_teste}`}
        small3={`Escolaridade: ${state.escolaridade}`}
        small4={`Data do teste: ${state.data_teste}`}
        exportpdf={!isAgeValid ? undefined : handleExportPDF}
        icon={true}
      >
        <FaUserAlt />
      </TopPage>

      <div className="container" ref={targetRef}>
        <div className="diagnostico-paciente-content" ref={refShowPdf}>
          <h1>Resultados da avaliação</h1>
          <h2>
            <b>Paciente:</b> {state.nome_paciente}
          </h2>
          <h2>
            <b>Data do teste:</b> {state.data_teste}
          </h2>
          <h2>
            <b>Idade ao realizar o teste:</b> {state.idade_teste}
          </h2>
          <h2>
            <b>Escolaridade:</b> {state.escolaridade}
          </h2>
          <h2>
            <b>Data de nascimento:</b> {state.nascimento} - {state.idade}
          </h2>

          <hr />
        </div>

        {!isAgeValid ? (
          <Alert
            tipo="warning center no-horizontal-margin"
            messages={[
              'O teste é aplicável de 11 a 30 anos. Não há tabelas normativas para a faixa etária informada. Assim, não podem ser oferecidas pontuações-padrão e classificação para esse paciente.',
            ]}
          />
        ) : (
          <Fragment>
            {state.need_validation && (
              <Alert
                tipo="warning center no-horizontal-margin"
                messages={[
                  'Para que as pontuações-padrão sejam calculadas com segurança, é preciso validar a correção automática dos escores e dos tempos de reação feita pela plataforma. Revise e valide as pontuações em cada módulo do instrumento.',
                ]}
              />
            )}

            <div className="diagnostico-paciente">
              <div className="diagnostico-title">Total de Acertos</div>
              <div className="diagnostico-list">
                <div className="column-1"></div>
                <div className="column-2">
                  <div className="diagnostico-title">Total de Acertos</div>
                </div>
                <div className="column-3">
                  <div className="diagnostico-title">Observação</div>
                </div>
              </div>

              <div className="diagnostico-list">
                <div className="column-1">Módulo 1</div>
                <div className="column-2">{state.acertos[0]}</div>
                <div className="column-3">
                  {state.acertos[0] < 23
                    ? 'Pontuação no Módulo 1 é inferior a 23. Não há evidência de automatização dos processos de leitura. Desempenhos no instrumento são inválidos.'
                    : '-'}
                </div>
              </div>
              <div className="diagnostico-list">
                <div className="column-1">Módulo 2</div>
                <div className="column-2">{state.acertos[1]}</div>
                <div className="column-3">
                  {state.acertos[1] < 23
                    ? 'Pontuação no Módulo 2 é inferior a 23. Desempenhos no instrumento não são válidos.'
                    : state.acertos[1] === 23
                      ? 'Seguir para interpretação com cautela. Desempenho na parte 2 no limite do esperado pode interferir no efeito Stroop'
                      : '-'}
                </div>
              </div>

              <div className="diagnostico-list four-columns">
                <div className="column-1"></div>
                <div className="column-2">
                  <div className="diagnostico-title">Total de Acertos</div>
                </div>
                <div className="column-3">
                  <div className="diagnostico-title">Pontuação-padrão</div>
                </div>
                <div className="column-4">
                  <div className="diagnostico-title">Classificação</div>
                </div>
              </div>

              <div className="diagnostico-list four-columns">
                <div className="column-1">Módulo 3</div>
                <div className="column-2">{state.acertos[2]}</div>
                <div className="column-3">{state.pontuacao[0] ?? '-'}</div>
                <div className="column-4">{state.classificacao[0]}</div>
              </div>
            </div>

            <div className="diagnostico-paciente">
              <div className="diagnostico-title">Média de TR de Acertos</div>

              <div className="diagnostico-list four-columns">
                <div className="column-1"></div>
                <div className="column-2">
                  <div className="diagnostico-title">Tempo de Reação</div>
                </div>
                <div className="column-3">
                  <div className="diagnostico-title">Pontuação-padrão</div>
                </div>
                <div className="column-4">
                  <div className="diagnostico-title">Classificação</div>
                </div>
              </div>
              <div className="diagnostico-list four-columns">
                <div className="column-1">Módulo 2</div>
                <div className="column-2">
                  {state.medias[0]
                    ? parseFloat(state.medias[0]).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      }) + ' s'
                    : '-'}
                </div>
                <div className="column-3">{state.pontuacao[1] ?? '-'}</div>
                <div className="column-4">{state.classificacao[1]}</div>
              </div>
              <div className="diagnostico-list four-columns">
                <div className="column-1">Módulo 3</div>
                <div className="column-2">
                  {state.medias[1]
                    ? parseFloat(state.medias[1]).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      }) + ' s'
                    : '-'}
                </div>
                <div className="column-3">{state.pontuacao[2] ?? '-'}</div>
                <div className="column-4">{state.classificacao[2]}</div>
              </div>
            </div>

            <div className="diagnostico-paciente">
              {state.indices[0] > 0 && (
                <Alert
                  tipo="warning center no-horizontal-margin"
                  messages={[
                    "<b>Interferência para Acertos</b>: Pontuações maiores do que 'zero' indicam desempenho pior na parte 2 do que na parte 3, o que não é esperado. Escore de interferência não é válido. Investigue possíveis razões para dificuldades na etapa de nomeação de cores.",
                  ]}
                />
              )}
              <div className="diagnostico-title">Índice de Interferência</div>
              <div className="diagnostico-list four-columns">
                <div className="column-1"></div>
                <div className="column-2">
                  <div className="diagnostico-title">
                    Índice de Interferência Bruto
                  </div>
                </div>
                <div className="column-3">
                  <div className="diagnostico-title">Pontuação-padrão</div>
                </div>
                <div className="column-4">
                  <div className="diagnostico-title">Classificação</div>
                </div>
              </div>
              <div className="diagnostico-list four-columns">
                <div className="column-1">Interferência para Acertos</div>
                <div className="column-2">{state.indices[0]}</div>
                <div className="column-3">{state.pontuacao[3] ?? '-'}</div>
                <div className="column-4">{state.classificacao[3]}</div>
              </div>
              <div className="diagnostico-list four-columns">
                <div className="column-1">Interferência para TR Acertos</div>
                <div className="column-2">
                  {state.indices[1]
                    ? parseFloat(state.indices[1]).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) + ' s'
                    : '-'}
                </div>
                <div className="column-3">{state.pontuacao[4] ?? '-'}</div>
                <div className="column-4">{state.classificacao[4]}</div>
              </div>
            </div>

            <div className="diagnostico-paciente">
              <div className="diagnostico-title">Índice de Variabilidade</div>
              <div className="diagnostico-list four-columns">
                <div className="column-1"></div>
                <div className="column-2">
                  <div className="diagnostico-title">
                    Índice de Variabilidade Bruto
                  </div>
                </div>
                <div className="column-3">
                  <div className="diagnostico-title">Pontuação-padrão</div>
                </div>
                <div className="column-4">
                  <div className="diagnostico-title">Classificação</div>
                </div>
              </div>
              <div className="diagnostico-list four-columns">
                <div className="column-1">Módulo 2</div>
                <div className="column-2">
                  {state.variabilidade[0]
                    ? parseFloat(state.variabilidade[0]).toLocaleString(
                        'pt-BR',
                        {
                          minimumFractionDigits: 2,
                        },
                      ) + ' s'
                    : '-'}
                </div>
                <div className="column-3">{state.pontuacao[5] ?? '-'}</div>
                <div className="column-4">{state.classificacao[5]}</div>
              </div>
              <div className="diagnostico-list four-columns">
                <div className="column-1">Módulo 3</div>
                <div className="column-2">
                  {state.variabilidade[1]
                    ? parseFloat(state.variabilidade[1]).toLocaleString(
                        'pt-BR',
                        {
                          minimumFractionDigits: 2,
                        },
                      ) + ' s'
                    : '-'}
                </div>
                <div className="column-3">{state.pontuacao[6] ?? '-'}</div>
                <div className="column-4">{state.classificacao[6]}</div>
              </div>
            </div>
          </Fragment>
        )}
      </div>

      {state.isLoading ? <Loading /> : null}

      <Footer />
    </div>
  );
};

export default DiagnosticoPaciente;
